import Vue from "vue";
import Vuex from "vuex";
import { Base64 } from "js-base64";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    // Site version
    version: "",
    versionNumeric: "",
    // Whether this is is a development instance of the site
    isDevelopment: false,
    // Current CSRF token
    csrfToken: "",
    // Site configuration
    config: { fetched: false },
    // Default editor configuration (TinyMCE)
    // See https://www.tiny.cloud/docs/configure/editor-appearance/
    editorConfig: {
      relative_urls: false,
      // menubar: false,
      menubar: "file edit",
      // See https://www.tiny.cloud/docs/configure/editor-appearance/#style_formats
      style_formats: [
        { title: "Heading 1", format: "h1" },
        { title: "Heading 2", format: "h2" },
        { title: "Heading 3", format: "h3" },
        { title: "Paragraph", format: "p" },
      ],
      plugins: "lists paste", // IMPORTANT - any plugins listed here must be imported in main.js
      // toolbar: "undo redo | styleselect | bold italic underline | numlist bullist",
      toolbar: "styleselect | bold italic underline | numlist bullist",
      skin: false, // Prevent editor trying to load theme and skin files - these are imported directly
      content_css: "/theme/editor", // IMPORTANT - if this path is changed, also change in setVersion() mutation below
      paste_as_text: true,
      // height: 450,
    },
    // The CSRF hash for POST requests
    // csrf_hash: "",
    // Details about the currently logged-in user
    user: { fetched: false },
    // Details about the PDL Template
    pdlTemplate: { fetched: false },
    // Pages for display in the navigation
    pages: { fetched: false, pages: [] },
    // Current user's CPD details
    cpd: { total: 0 },
    // Current page's banner image
    banner: false,
    // Current page's background image
    background: "",
    // Current page's breadcrumbs
    breadcrumbs: [],
    // Current page's title
    title: { title: "", titleTranslated: false, subtitle: "" },
  },
  mutations: {
    /**
     * Set the version number.
     * @param {*} state
     * @param {*} payload
     */
    setVersion(state, payload) {
      state.version = payload;

      // Cache busting
      const versionNumeric = payload.replace(/\./g, "");
      state.versionNumeric = versionNumeric;
      state.editorConfig.content_css = `/theme/editor?nc=${versionNumeric}`;
    },

    /**
     * Set the development flag, indicating whether this is a development instance of the site.
     * @param {*} state
     * @param {*} payload
     */
    setIsDevelopment(state, payload) {
      state.isDevelopment = payload;
    },

    /**
     * Set the current CSRF token.
     * @param {*} state
     * @param {*} payload
     */
    setCsrfToken(state, payload) {
      state.csrfToken = payload;
    },

    /**
     * Set the site configuration.
     * @param {*} state
     * @param {*} payload Object of key-value pairs containing site configuration information.
     */
    setConfig(state, payload) {
      if (payload.self_registration_contact) {
        payload.self_registration_contact = Base64.decode(payload.self_registration_contact);
      }
      state.config = { ...payload, fetched: true };
    },

    /**
     * Update the CSRF Hash
     * @param {*} state
     * @param {String} payload CodeIgniter CSRF hash.
     */
    // setCsrfHash(state, payload) {
    //   state.csrf_hash = payload;
    // },

    /**
     * Set the currently logged in user's information.
     * @param {*} state
     * @param {*} payload Object of key-value pairs containing uyser information.
     */
    setUser(state, payload) {
      state.user = { ...payload, fetched: true };
    },

    /**
     * Set the user's force_password_change flag.
     * @param {*} state
     * @param {Boolean} payload
     */
    setUserForcePasswordChanged(state, payload) {
      state.user.force_password_change = payload;
    },

    /**
     * Set the current PDL template
     * @param {*} state
     * @param {*} payload
     */
    setPdlTemplate(state, payload) {
      state.pdlTemplate = { ...payload, fetched: true };
    },

    /**
     * Clear the next flag so that route redirections do not happen anymore.
     * @param {*} state
     */
    clearUserNext(state) {
      state.user.next = "";
    },

    /**
     * Modify editorConfig to give curators (and by extension admins) additional capabilities.
     * @param {*} state
     * @param {*} payload
     */
    setCuratorEditorConfig(state) {
      state.editorConfig.plugins = `${state.editorConfig.plugins} link`;
      state.editorConfig.toolbar = `${state.editorConfig.toolbar} | link`;
    },

    /**
     * Set the pages which should be shown in the site navigation.
     * @param {*} state
     * @param {*} payload List of pages which the user has access to.
     */
    setPages(state, payload) {
      state.pages = { pages: payload, fetched: true };
    },

    /**
     * Set CPD details for the currently logged in user.
     * @param {*} state
     * @param {*} payload
     */
    setCpd(state, payload) {
      state.cpd = payload;
    },

    /**
     * Set the current breadcrumbs to display on page.
     * @param {*} state
     * @param {*} payload Array containing breadcrumb information, in the form [{url: "...", text: "..."}, {...}]
     */
    setBreadcrumbs(state, payload) {
      state.breadcrumbs = payload;
    },

    /**
     * Update the target on a specified breadcrumb.
     * @param {*} state
     * @param {*} payload Object containing new target information, in form { index, vueRouterTarget }
     */
    setBreadcrumbTarget(state, payload) {
      if (state.breadcrumbs[payload.index]) {
        if (Object.prototype.hasOwnProperty.call(payload, "textTranslated")) {
          state.breadcrumbs[payload.index].textTranslated = payload.textTranslated;
        }
        if (Object.prototype.hasOwnProperty.call(payload, "text")) {
          state.breadcrumbs[payload.index].text = payload.text;
        }
        if (Object.prototype.hasOwnProperty.call(payload, "target")) {
          state.breadcrumbs[payload.index].target = payload.target;
        }
      }
    },

    /**
     * Clear the breadcrumb information.
     * @param {*} state
     */
    clearBreadcrumbs(state) {
      state.breadcrumbs = [];
    },

    /**
     * Set the current banner image.
     * @param {*} state
     * @param {String} payload
     */
    setBanner(state, payload) {
      state.banner = payload;
    },

    /**
     * Clear the banner image.
     * @param {*} state
     */
    clearBanner(state) {
      state.banner = false;
    },

    /**
     * Set the current background image (applied to body).
     * @param {*} state
     * @param {String} payload
     */
    setBackground(state, payload) {
      state.background = payload;
      // TODO - it feels wrong doing this here - possibly have some utility method that does it?
      document.body.classList.add("with-background");
      document.body.style.backgroundImage = `url("${payload}")`;
    },

    /**
     * Clear the background image.
     * This method is called on each route change.
     * @param {*} state
     */
    clearBackground(state) {
      state.background = "";
      // TODO - it feels wrong doing this here - possibly have some utility method that does it?
      document.body.classList.remove("with-background");
      document.body.style.backgroundImage = "none";
    },

    /**
     * Set the current title and subtitle.
     * @param {*} state
     * @param {Array} payload
     */
    setTitle(state, payload) {
      state.title = payload;
    },

    /**
     * Clear the current title and subtitle.
     * @param {Array} state
     */
    clearTitle(state) {
      state.title = { title: "", titleTranslated: false, subtitle: "" };
    },

    /**
     * Set the banner subtitle directly, i.e. the second element in the title array.
     * @param {*} state
     * @param {string} payload
     */
    setSubTitle(state, payload) {
      state.title.subtitle = payload;
    },
  },
  actions: {},
});
